// src/components/layouts/SynthwaveLayout.tsx

import React, { ReactNode, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './SynthwaveLayout.css';
import logoblast from './images/logoblast.png';
import { useAuth } from '../../contexts/AuthContext'; // Import useAuth

interface SynthwaveLayoutProps {
  children: ReactNode;
  title?: string;
}

const SynthwaveLayout: React.FC<SynthwaveLayoutProps> = ({
  children,
  title = 'BLAST',
}) => {
  const { logout } = useAuth(); // Get the logout function from auth context
  const navigate = useNavigate(); // For redirecting after logout

  const backgroundImages = [
    '/images/80sSpaceTheme1.png',
    '/images/rainbowblastbackground.png',
    '/images/whaleimageblastbackground.png',
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  // Handle logout function
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/'); // Redirect to landing page after logout
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  // Close menu if clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (menuOpen &&
          !target.closest('.hamburger-menu') &&
          !target.closest('.menu-container')) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  // Cycle background images
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % backgroundImages.length);
    }, 10000); // 10 seconds

    return () => clearInterval(intervalId);
  }, []);

  return (
    <div
      className="synthwave-container"
      style={{
        backgroundImage: `url("${backgroundImages[currentImageIndex]}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        overflowY: 'auto',
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        transition: 'background-image 1s ease-in-out',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          minHeight: '100px',
          marginTop: '10px',
        }}
      >
        {/* Centered Logo */}
        <img
          src={logoblast}
          alt={title}
          style={{
            display: 'block',
            margin: '0 auto',
            maxHeight: '80px',
            width: 'auto',
          }}
          className="logo-image"
        />

        {/* Hamburger button */}
        <div
          className="hamburger-menu"
          onClick={toggleMenu}
          style={{
            position: 'absolute',
            top: '10px',
            right: '20px',
            width: '45px',
            height: '45px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '4px solid black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '5px',
            zIndex: 30,
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = 'translateY(1px)';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
        >
          <div
              className="hamburger-bar"
              style={{
                width: '24px',
                height: '3px',
                backgroundColor: 'black', /* Changed from '#ffee6d' to 'black' */
                marginBottom: '5px',
                borderRadius: '2px',
              }}
            />
            <div
              className="hamburger-bar"
              style={{
                width: '24px',
                height: '3px',
                backgroundColor: 'black', /* Changed from '#ffee6d' to 'black' */
                marginBottom: '5px',
                borderRadius: '2px',
              }}
            />
            <div
              className="hamburger-bar"
              style={{
                width: '24px',
                height: '3px',
                backgroundColor: 'black', /* Changed from '#ffee6d' to 'black' */
                borderRadius: '2px',
              }}
            />
        </div>

        {/* Dropdown Menu */}
        {menuOpen && (
          <div
            className="menu-container"
            style={{
              position: 'absolute',
              top: '65px',
              right: '20px',
              backgroundColor: '#ffee6d',
              border: '4px solid black',
              borderRadius: '8px',
              zIndex: 40,
              minWidth: '200px',
              animation: 'slideDown 0.3s ease-out forwards',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}
          >
            <Link
              to="/dashboard"
              className="menu-item"
              style={{
                display: 'block',
                padding: '12px 16px',
                borderBottom: '3px solid black',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                backgroundColor: 'white',
                color: 'black',
                textDecoration: 'none',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
              onClick={() => setMenuOpen(false)}
            >
              Dashboard
            </Link>
            <Link
              to="/create-event"
              className="menu-item"
              style={{
                display: 'block',
                padding: '12px 16px',
                borderBottom: '3px solid black',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                backgroundColor: 'white',
                color: 'black',
                textDecoration: 'none',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
              onClick={() => setMenuOpen(false)}
            >
              Create Event
            </Link>
            <a
              href="#"
              className="menu-item"
              style={{
                display: 'block',
                padding: '12px 16px',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                backgroundColor: 'white',
                color: 'black',
                textDecoration: 'none',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
              onClick={(e) => {
                e.preventDefault();
                handleLogout();
                setMenuOpen(false);
              }}
            >
              Log Out
            </a>
          </div>
        )}
      </div>

      {/* Optional spacing before main content */}
      <div style={{ height: '20px' }} />

      {/* MAIN CONTENT */}
      {children}
    </div>
  );
};

export default SynthwaveLayout;